import type { LandingContent } from "./types";
import heroImage from "@/assets/hero-house.webp";
import extractionSequence from "@/assets/lp/diagrams/extraction-sequence.webp";
import failureDamp from "@/assets/lp/failure/damp-problems.webp";
import failureColdWalls from "@/assets/lp/failure/cold-walls.webp";
import failurePrevious from "@/assets/lp/failure/previous-insulation.webp";
import failureCracked from "@/assets/lp/failure/cracked-rendering.webp";

export const cavityInsulationRemovalLp: LandingContent = {
  slug: "cavity-extraction",
  path: "/lp/cavity-insulation-removal",
  title: "Cavity Wall Insulation Removal In South Wales",
  metaTitle: "Cavity Wall Insulation Removal South Wales | Specialist Extraction Service",
  metaDescription:
    "Specialist cavity wall insulation removal and extraction in South Wales. Free no-obligation quotation, certified installers.",
  defaultService: "Cavity Wall Insulation Removal",
  heroHeadline: "Cavity Wall Insulation Removal In South Wales",
  heroSubheading:
    "If your cavity wall insulation has failed, we specialise in removing your old insulation system, across Wales and the South of England.",
  heroBullets: [
    "Specialist Extraction Equipment",
    "Cavity Inspection First",
    "EPS Bonded Bead Re-Fill",
  ],
  heroImage,
  heroAlt: "South Wales home where CRS Energy carried out cavity wall insulation removal",
  trustItems: [
    "PAS 2030 Certified Installations",
    "25-Year Guarantee",
    "Insurance-Backed",
    "Free Quotation",
    "No Obligation",
  ],
  premiseLine: {
    title: "Inspection Before Extraction. If Your Problem Isn't Cavity Insulation, We'll Tell You.",
    sub: "We don't assume extraction is needed - and we won't sell you a service you don't need.",
  },
  featureStrip: [
    {
      icon: "stethoscope",
      title: "Cavity Inspection First",
      body: "We identify the actual cause of damp or cold walls before recommending any work. Sometimes the issue isn't cavity insulation at all.",
    },
    {
      icon: "brick",
      title: "Specialist Extraction Equipment",
      body: "Mechanical vacuum extraction carried out from outside your property. No internal disruption.",
    },
    {
      icon: "shield",
      title: "Optional Re-Insulation Available",
      body: "If requested, we can discuss EPS bonded bead re-insulation after extraction, which comes with a 25-year guarantee.",
    },
  ],
  problemTitle: "When Cavity Wall Insulation Fails",
  problemPoints: [
    {
      title: "Damp Problems Linked To Existing Insulation",
      body: "Some older cavity fill materials can absorb moisture and transfer it to your internal walls. This causes damp patches, peeling paint, and mould growth.",
    },
    {
      title: "Cold Walls Despite Having Insulation",
      body: "If insulation has slumped, settled, or degraded over time, it may no longer provide effective thermal protection. You may notice cold spots or uneven room temperatures.",
    },
    {
      title: "Previous Insulation Installed Incorrectly",
      body: "Not all installations follow best practice. Poorly installed cavity insulation can cause ongoing issues that worsen over time.",
    },
  ],
  clarityTitle: "How Do We Extract Cavity Wall Insulation?",
  clarityBody:
    "Removing cavity wall insulation offers key benefits, including the opportunity to assess and evaluate the condition of your wall cavity. After extraction, we conduct a comprehensive report to examine wall ties, identify any existing rubble, and determine if reinsulating is desired. This allows us to assess whether the walls need reinforcement and estimate the time required for re-insulation with bonded beads. \n\nThe extraction process itself is straightforward, performed by specialists using specialised equipment to carefully remove the insulation through small holes drilled in the external brickwork. The cavity can then be left empty or refilled with a suitable material, such as EPS-bonded beads, which are water-repellent and BBA-certified - ideal for exposed locations. \n\nTypically, the entire process takes 1-2 days with a friendly team of 2-3 technicians. Each property is unique, so we tailor our approach based on your specific needs discussed during the initial survey. Plus, every extraction comes with a reassuring 2-year guarantee, and payment is only required once the work is complete.",
  clarityImage: extractionSequence,
  clarityImageAlt:
    "Diagram showing the three stages of cavity insulation extraction: drilling access holes, vacuum removal of failed insulation, and re-fill with new EPS bonded beads.",
  productTitle: "Why Consider Reinsulating With A Bonded-Bead Insulation System",
  productPoints: [
    {
      title: "Removes The Root Cause Of Damp",
      body: "If your cavity fill is bridging moisture, removing it stops the problem at source - rather than treating symptoms on the inside.",
    },
    {
      title: "Restores Thermal Performance",
      body: "A fresh EPS bonded bead fill provides consistent, reliable insulation designed to maintain performance and resist moisture."
    },
    {
      title: "Controlled, External Process",
      body: "Extraction is carried out from outside your property using industrial vacuum equipment. There's no disruption inside your home.",
    },
    {
      title: "Documented From Start To Finish",
      body: "You receive a clear record of what was found, what was removed, and what was installed - including full guarantee documentation.",
    },
  ],
  processSteps: [
    {
      step: 1,
      title: "Cavity Inspection",
      description:
        "We inspect your property to identify the existing fill type, assess moisture levels, and determine whether extraction is the right course of action.",
      icon: "moisture",
    },
    {
      step: 2,
      title: "Extraction Plan & Quote",
      description:
        "After your inspection, one of our friendly advisors will give you a call to discuss the findings, explain your clear quote, and talk you through what happens next. There is no obligation to proceed.",
      icon: "file",
    },
    {
      step: 3,
      title: "Extraction & Report",
      description:
        "We remove failed material with specialist vacuum equipment. The report will provide clarity on the cavity's condition post-extraction.",
      icon: "vacuum",
    },
    {
      step: 4,
      title: "Guarantee & Documentation",
      description:
        "You receive full completion records, including guarantees and warranties.",
      icon: "certificate",
    },
  ],
  qualificationTitle: "Signs Your Cavity Insulation May Have Failed",
  qualificationItems: [
    "Damp patches appearing on internal walls",
    "Mould growth in rooms that were previously dry",
    "Cold walls even though insulation is present",
    "Peeling paint or wallpaper on external-facing walls",
    "Insulation was installed more than 15 years ago",
    "A previous inspector has recommended extraction",
  ],
  includedItems: [
    "Pre-extraction cavity inspection",
    "Mechanical cavity extraction (external process)",
    "Cavity clearance verification",
    "EPS bonded bead re-fill (if cavity is being re-insulated)",
    "Post-installation quality check",
    "Building regulations certificate (where applicable)",
    "25-year CIGA guarantee on new fill",
    "Full documentation pack (before, during, after)",
  ],
  trustSectionTitle: "Diagnosis Before Remediation",
  trustSectionBody:
    "We don't assume extraction is needed. Our inspection determines the root cause first. If the problem is not related to cavity insulation, we'll tell you. All extraction and re-fill work is carried out under PAS 2030 with independent guarantees.",
  benefits: [
    { title: "Resolve Persistent Damp Issues", description: "Remove the failed material causing moisture transfer through your walls." },
    { title: "Restore Proper Thermal Insulation", description: "Replace inefficient fill with a modern, certified system." },
    { title: "No Internal Disruption", description: "All extraction and installation work is carried out from outside your property." },
    { title: "Full Documentation And Guarantees", description: "Clear records of what was done and why, with long-term warranty protection." },
  ],
  // NOTE: Until extraction-specific testimonials are sourced, we display the
  // single most-relevant cavity-wall testimonial we have (Liz S). Per brief,
  // better one relevant testimonial than three off-topic ones.
  testimonialsFull: [
    {
      name: "Liz S",
      date: "February 2025",
      serviceTag: "Cavity Wall, Damp & Mould Resolved",
      source: "google",
      quote:
        "We had cavity wall insulation done which has made a big difference to our house. Our rooms were cold and we had some mould in corners of the rooms. Since having it done there hasn't been any. House warms up quicker and stays warmer longer. So glad we had it done and it was quick and easy. CRS were lovely and I would recommend them to anyone.",
    },
    {
      name: "Kevin Stevens",
      date: "September 2024",
      serviceTag: "Boiler & Wall Insulation",
      source: "trustpilot",
      quote:
        "This company carried out the work on our house for a Welsh government grant scheme, the work was carried out between July and September for a new boiler and internal wall and cavity wall insulation. Every one of the workers in the different teams, including the plumbers, electricians, insulation team, carpenters, plasterers, and the painter were all very polite, friendly and respectful of our property. We would highly recommend them.",
    },
    {
      name: "Richard W",
      date: "September 2025",
      serviceTag: "Whole Home Retrofit",
      source: "google",
      quote:
        "I cannot praise this company too highly. From first contact to completion of the work the whole experience was perfect. People turned up when they were supposed to and were all friendly, good-humoured and respectful, and they knew what they were doing. All three teams that carried out the work were well coordinated and efficient. And they all cleared up after them! Great coordination between the office and the survey and work teams.",
    },
  ],
  faqs: [
    { question: "How do I know if my cavity insulation has failed?", answer: "Common signs include damp patches on internal walls, cold spots, mould growth, and peeling decoration on external-facing walls. Our inspection will confirm whether extraction is needed." },
    { question: "How long does cavity extraction take?", answer: "Extraction typically takes 1-2 days depending on the property size. Re-filling with EPS beads is usually completed on the same visit or the following day." },
    { question: "Will extraction damage my brickwork?", answer: "Small holes are drilled in the mortar joints to access the cavity. These are filled and pointed on completion, leaving a clean finish." },
    { question: "Do I have to re-fill the cavity after extraction?", answer: "Not necessarily. Some homeowners choose to leave the cavity empty. However, re-filling with a suitable material restores thermal insulation and is usually recommended." },
    { question: "What type of insulation caused the problem?", answer: "Common culprits include mineral wool, urea formaldehyde foam, and some older bead systems that weren't bonded. Our inspection identifies the material and the issue." },
    { question: "What guarantee do I get after re-fill?", answer: "You receive a 25-year CIGA guarantee and insurance-backed warranty on the new EPS bonded bead installation." },
  ],
  ctaTitle: "Book Your Free Extraction Inspection",
  failureAccordion: {
    title: "Is Your Cavity Wall Insulation Failing?",
    subtitle:
      "Damp, cold walls, mould, or staining can have several causes. During an inspection, our surveyors look for signs that existing cavity wall insulation may be contributing to the issue.",
    ctaText: "Book A Free Quotation",
    ctaHref: "#bottom-cta",
    rows: [
      {
        icon: "droplets",
        title: "Damp Problems Linked To Existing Insulation",
        teaser: "Older cavity fill can absorb moisture and transfer it to your internal walls.",
        body: "Older cavity fill, particularly mineral fibre installed before 2010, can absorb moisture and bridge the cavity, transferring damp to your internal walls. No surface treatment will permanently fix it.",
        symptomTiles: [
          { icon: "droplets", label: "Damp patches on internal walls" },
          { icon: "circle-dashed", label: "Mould growth in previously dry rooms" },
          { icon: "paintbrush", label: "Peeling paint or wallpaper on external-facing walls" },
          { icon: "wind", label: "Persistent musty smells" },
        ],
        image: failureDamp,
        imageAlt: "Damp staining and moisture readings on an internal wall affected by failed cavity insulation",
      },
      {
        icon: "alert-triangle",
        title: "Previous Insulation Installed Incorrectly",
        teaser: "Not all installations follow best practice, so poor work causes ongoing issues that worsen over time.",
        body: "Not all installations follow best practice. Properties insulated over 15 years ago, or in exposed locations without proper assessment, are most prone to slow-developing failures that only become visible once damage has spread.",
        symptomTiles: [
          { icon: "calendar-clock", label: "Insulation installed more than 15 years ago" },
          { icon: "file-search", label: "A previous inspector recommended extraction" },
          { icon: "target", label: "Visible drill-hole patches in irregular patterns" },
          { icon: "mountain", label: "Property in an exposed coastal or hilltop location" },
        ],
        image: failurePrevious,
        imageAlt: "CRS Energy surveyor using a thermal camera to inspect a wall with poorly installed cavity insulation",
      },
      {
        icon: "thermometer-snowflake",
        title: "Cold Walls Despite Having Insulation",
        teaser: "If insulation has slumped, settled or degraded, it can no longer protect your home.",
        body: "Cavity insulation should keep walls warm and rooms evenly heated. If it has slumped, settled or degraded, voids form that let cold penetrate, even when insulation is technically still there.",
        symptomTiles: [
          { icon: "snowflake", label: "Cold walls even though insulation is present" },
          { icon: "thermometer", label: "Uneven room temperatures or cold spots" },
          { icon: "flame", label: "Higher heating bills than expected" },
          { icon: "clock", label: "Rooms slow to warm up" },
        ],
        image: failureColdWalls,
        imageAlt: "Mould and cold-bridging around a socket on a wall with failed cavity insulation",
      },
      {
        icon: "alert-octagon",
        title: "Lateral Cracking Of Render Or Mortar Joints",
        teaser: "Horizontal cracks in render or mortar let water into the cavity, accelerating insulation failure.",
        body: "Horizontal cracks in external render or mortar are a strong sign that water is reaching the cavity. Once it's in, it saturates the insulation and accelerates every other failure mode, making this one of the most important external signs to spot early.",
        symptomTiles: [
          { icon: "minus", label: "Horizontal cracks in external render" },
          { icon: "grip", label: "Crumbling or eroded mortar between bricks" },
          { icon: "sparkles", label: "Staining, bulging or efflorescence (white salt deposits)" },
          { icon: "volume-2", label: "Render that sounds hollow when tapped" },
        ],
        image: failureCracked,
        imageAlt: "Lateral crack running across the external render of a gable wall, allowing water into the cavity",
      },
    ],
  },
  processClarity: {
    title: "How We Assess & Remove Failed Cavity Insulation",
    intro:
      "Extraction not only removes failed insulation, it gives a clear view of the cavity condition behind the walls.",
    diagramImage: extractionSequence,
    stepTexts: [
      "Small access holes, around 25mm, are drilled into the mortar joints of the external brickwork. No internal disruption to your home, and we re-point every hole when the job is finished.",
      "Specialist high-pressure vacuum equipment removes the failed insulation from inside the cavity. We capture every fibre or bead and dispose of the material responsibly.",
      "If a refill is the right choice, we use BBA-certified, water-repellent EPS bonded beads, engineered to suit exposed South Wales locations and not slump or absorb moisture.",
    ],
    callout: {
      title: "Concerned About What May Be Hidden Inside The Walls? We Document It For You.",
      body: "Following extraction, we provide a photographic cavity condition report so you have a clearer understanding of what's happening behind the brickwork.",
    },
    facts: [
      { icon: "clock", label: "Timeline", value: "1 to 2 Days" },
      { icon: "users", label: "Team", value: "2 to 3 Specialists" },
      { icon: "package", label: "Refill Material", value: "EPS Bonded Bead Water Repellent Insulation", badge: "Optional" },
      { icon: "shield-check", label: "Guarantee", value: "2 Years on Extraction" },
      { icon: "credit-card", label: "Payment", value: "Only When Work Is Complete" },
    ],
    ctaText: "Book A Free Quotation",
    ctaHref: "#bottom-cta",
  },
  // Extraction page section order:
  // Hero → premise strip → feature strip → accreditations →
  // Failure accordion → Process clarity (diagram + facts) → Process steps
  // → Why Extract → Trust → Testimonials → Included → Pricing → Help → FAQ → Form
  sectionOrder: [
    "failureAccordion",
    "processClarity",
    "process",
    "product",
    "trust",
    "testimonials",
    "pricing",
    "ctaLight",
    "faq",
  ],
};
